import type { EventBus } from '../../game/events/EventBus';
import type { DialogueService } from '../dialogue/DialogueService';
import type { InventoryService } from '../inventory/InventoryService';
import type { ConditionEvaluator } from '../rules/ConditionEvaluator';
import type { GameStateStore } from '../state/GameStateStore';
import type { ActionDefinition, EffectDefinition } from '../types';

export interface SceneNavigator {
  openScene(sceneId: string, locationId: string): void;
  returnToMap(): void;
  playAnimation(animationId: string): void;
}

export class EffectExecutor {
  constructor(
    private readonly store: GameStateStore,
    private readonly inventory: InventoryService,
    private readonly evaluator: ConditionEvaluator,
    private readonly dialogue: DialogueService,
    private readonly events: EventBus,
    private readonly navigator: SceneNavigator,
  ) {}

  async executeAction(action: ActionDefinition): Promise<boolean> {
    if (!this.evaluator.evaluateAll(action.conditions, this.store.snapshot)) {
      this.events.emit('message:show', {
        text: action.unavailableMessage ?? 'Det hjælper ikke her.',
      });
      return false;
    }
    await this.executeAll(action.effects);
    return true;
  }

  async executeAll(effects: EffectDefinition[] = []): Promise<void> {
    for (const effect of effects) await this.execute(effect);
  }

  async execute(effect: EffectDefinition): Promise<void> {
    switch (effect.type) {
      case 'message':
        this.events.emit('message:show', { text: effect.text });
        break;
      case 'startDialogue':
        this.dialogue.start(effect.dialogueId);
        break;
      case 'setFlag':
        this.store.setFlag(effect.key, effect.value);
        break;
      case 'setSceneState':
        this.store.setSceneState(effect.sceneId, effect.key, effect.value);
        break;
      case 'addItem':
        if (this.inventory.add(effect.itemId)) {
          this.events.emit('message:show', {
            text: effect.message ?? 'Genstanden er lagt i rygsækken.',
            kind: 'item',
          });
        }
        break;
      case 'removeItem':
        this.inventory.remove(effect.itemId);
        break;
      case 'playAnimation':
        this.navigator.playAnimation(effect.animationId);
        break;
      case 'changeHotspot':
        this.store.setFlag(`hotspot:${effect.hotspotId}:enabled`, effect.enabled);
        break;
      case 'unlockLocation':
        this.store.update((draft) => {
          if (!draft.unlockedLocations.includes(effect.locationId)) {
            draft.unlockedLocations.push(effect.locationId);
          }
        });
        break;
      case 'markPuzzleSolved':
        this.store.update((draft) => {
          if (!draft.completedPuzzles.includes(effect.puzzleId)) {
            draft.completedPuzzles.push(effect.puzzleId);
          }
        });
        break;
      case 'transitionToScene':
        this.navigator.openScene(effect.sceneId, effect.locationId);
        break;
      case 'returnToMap':
        this.navigator.returnToMap();
        break;
      case 'playSound':
        this.events.emit('audio:sfx', { soundId: effect.soundId });
        break;
      case 'delay':
        await new Promise((resolve) => window.setTimeout(resolve, effect.milliseconds));
        break;
      case 'sequence':
        await this.executeAll(effect.effects);
        break;
    }
  }
}
